/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisMigratedAppStore } from 'src/clu/core/stores/LuisMigratedAppStore';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { CluMigrationModalStepsNavigator } from 'src/luis/verticals/cluMigrationModal/components/CluMigrationModalStepsNavigator';
import { MigrationOverviewScreen } from 'src/luis/verticals/cluMigrationModal/components/MigrationOverviewScreen';
import { ReviewAndFinishScreen } from 'src/luis/verticals/cluMigrationModal/components/ReviewAndFinishScreen';
import { SelectLanguageResourceScreen } from 'src/luis/verticals/cluMigrationModal/components/SelectLanguageResourceScreen';
import { SelectLuisApplicationsScreen } from 'src/luis/verticals/cluMigrationModal/components/SelectLuisApplicationsScreen';
import styled from 'styled-components';

export type CluMigrationInfoDto = {
    languageResource: AzureCsAccountStore;
    applications: LuisMigratedAppStore[];
};

/**
 * Determines the clu migration steps.
 */
export enum CluMigrationSteps {
    MigrationOverview = 0,
    SelectLanguageResource = 1,
    SelectLuisApplications = 2,
    ReviewAndFinish = 3
}

const messages = defineMessages({
    modalTitle: {
        id: 'cluMigration.modalTitle',
        defaultMessage: 'Migrate to conversational language understanding'
    },
    errorMessage: {
        id: 'cluMigration.errorMessage',
        defaultMessage:
            'Your role {role} does not have access to migrate applications. You must either be an Owner, Contributor, or LUIS Owner on the authoring resource'
    }
});

export const CluMigrationContentSideMargin = '32px';

const modalTheme = getScopedTheme('CluMigrationModalBase');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: borderStyle }
});
const ContentWrapper = styled(Stack)(() => ({
    paddingTop: '28px',
    width: '100%',
    minHeight: '600px',
    color: modalTheme.defaultTextColor,
    minWidth: 0
}));
const NavigatorWrapper = styled(Stack.Item)<{ horizontal: boolean }>(props => ({
    width: props.horizontal ? '300px' : '',
    borderRight: props.horizontal ? borderStyle : '',
    paddingTop: '34px'
}));

const getInitialCluMigrationDto: CluMigrationInfoDto = {
    languageResource: null,
    applications: []
};

/**
 * Represents the luis clu migration modal.
 */
export const CluMigrationModal = withModalMounter(
    observer((props: BasicModalProps<CluMigrationInfoDto>) => {
        const { onDismiss, onSubmit } = props;
        const { rootStore } = useGlobalState();
        const { localize } = useLocalization();
        const { isLargeOrGreater } = useResponsiveMode();

        const {
            cluMigrationStoreHouse: { selectedApps: applications }
        } = rootStore;

        const { displayedUserRole } = rootStore.serviceKey;

        const canUserMigrate =
            displayedUserRole === 'Owner' || displayedUserRole === 'Contributor' || displayedUserRole === 'Cognitive Services LUIS Owner';

        const [cluMigrationInfoDto, updateCluMigrationDto] = useUpdateState<CluMigrationInfoDto>({
            ...getInitialCluMigrationDto,
            applications: applications.map(
                app =>
                    ({
                        id: app.id,
                        key: app.id,
                        loadedVersions: [],
                        selectedVersion: app.activeVersion
                    } as LuisMigratedAppStore)
            )
        });
        const [activeStep, setActiveStep] = React.useState(CluMigrationSteps.MigrationOverview);

        const handleStepSubmissions = (dto?: Partial<CluMigrationInfoDto>) => {
            if (dto) {
                updateCluMigrationDto(dto);
            }
            setActiveStep(activeStep + 1);
        };

        const getStepContent = () => {
            switch (activeStep) {
                case CluMigrationSteps.MigrationOverview:
                    return <MigrationOverviewScreen onSubmit={handleStepSubmissions} onDismiss={onDismiss} isValid={canUserMigrate} />;
                case CluMigrationSteps.SelectLanguageResource:
                    return (
                        <SelectLanguageResourceScreen
                            onDismiss={onDismiss}
                            cluMigrationInfoDto={cluMigrationInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={languageResource => handleStepSubmissions({ languageResource })}
                        />
                    );
                case CluMigrationSteps.SelectLuisApplications:
                    return (
                        <SelectLuisApplicationsScreen
                            onDismiss={onDismiss}
                            cluMigrationInfoDto={cluMigrationInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={apps => handleStepSubmissions({ applications: apps })}
                        />
                    );
                case CluMigrationSteps.ReviewAndFinish:
                    return (
                        <ReviewAndFinishScreen
                            onDismiss={onDismiss}
                            cluMigrationInfoDto={cluMigrationInfoDto}
                            onPrevious={() => setActiveStep(activeStep - 1)}
                            onSubmit={() => {
                                onSubmit(cluMigrationInfoDto);
                                onDismiss();
                            }}
                        />
                    );
                default:
            }
        };

        return (
            <BasicModal
                {...props}
                minWidth={976}
                shouldRenderFooter={false}
                modalProps={{ isBlocking: true }}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}
                errorMessage={!canUserMigrate && localize(messages.errorMessage, { role: displayedUserRole })}>
                <Stack horizontal={isLargeOrGreater} verticalFill>
                    <NavigatorWrapper horizontal={isLargeOrGreater}>
                        <CluMigrationModalStepsNavigator
                            activeStep={activeStep}
                            horizontal={!isLargeOrGreater}
                            onActiveStepChange={setActiveStep}
                        />
                    </NavigatorWrapper>

                    <ContentWrapper grow verticalFill styles={{ root: { position: 'relative' } }}>
                        {getStepContent()}
                    </ContentWrapper>
                </Stack>
            </BasicModal>
        );
    })
);
